<template>
  <div class="login-page" @mousemove="onMouseMove">

    <!-- ── Decorative right panel ── -->
    <div class="login-deco" ref="decoPanel">
      <!-- Animated blobs -->
      <div class="ld-blob ld-blob-1"></div>
      <div class="ld-blob ld-blob-2"></div>
      <div class="ld-blob ld-blob-3"></div>

      <!-- Floating chemistry symbols -->
      <span class="lm lm-1">⚗</span>
      <span class="lm lm-2">⬡</span>
      <span class="lm lm-3">⚛</span>
      <span class="lm lm-4">H₂O</span>
      <span class="lm lm-5">🧪</span>
      <span class="lm lm-6">CO₂</span>
      <span class="lm lm-7">⬡</span>
      <span class="lm lm-8">Δ</span>
      <span class="lm lm-9">NaCl</span>
      <span class="lm lm-10">O₂</span>
      <span class="lm lm-11">⚛</span>
      <span class="lm lm-12">pH</span>

      <!-- Teacher avatar -->
      <div class="ld-avatar-wrap">
        <div class="ld-avatar-orbit ld-orbit-1"></div>
        <div class="ld-avatar-orbit ld-orbit-2"></div>
        <div class="ld-avatar-ring">
          <img src="@/assets/images/favicon.png" alt="أ/ مصطفى حسني" />
        </div>
      </div>

      <!-- Motivational text -->
      <div class="ld-text">
        <h1 class="ld-heading">ابدأ رحلتك في التفوق</h1>
        <p class="ld-sub">مع أ/ مصطفى حسني — مدرس الكيمياء</p>
        <div class="ld-badges">
          <span class="ld-badge">🏆 2000+ طالب ناجح</span>
          <span class="ld-badge">📚 350+ درس</span>
          <span class="ld-badge">⭐ 95% نجاح</span>
        </div>
      </div>
    </div>

    <!-- ── Form left panel ── -->
    <div class="login-form-panel">
      <div class="login-card" :class="{ 'login-card--shake': shaking }">

        <!-- Logo mark -->
        <div class="lc-logo">
          <img src="@/assets/images/favicon.png" alt="logo" />
        </div>

        <h2 class="lc-title">مرحباً بعودتك 👋</h2>
        <p class="lc-subtitle">سجّل دخولك للمتابعة</p>

        <form @submit.prevent="login" class="lc-form" novalidate>

          <!-- Reservation ID field -->
          <div class="lf-group" :class="{ 'lf-group--focus': phoneFocused, 'lf-group--filled': reservationNumber }">
            <label class="lf-label">رقم الحجز (Reservation ID)</label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="16" rx="2" ry="2"/>
                  <path d="M3 9h18M8 15h3"/>
                </svg>
              </span>
              <input
                type="text"
                v-model="reservationNumber"
                placeholder="مثال: 2479"
                class="lf-input"
                @focus="phoneFocused = true"
                @blur="phoneFocused = false"
                autocomplete="off"
                dir="ltr"
              />
            </div>
          </div>

          <!-- Password field (student's phone number) -->
          <div class="lf-group" :class="{ 'lf-group--focus': passFocused, 'lf-group--filled': password }">
            <label class="lf-label">رقم هاتف الطالب</label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </span>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="أدخل رقم هاتف الطالب"
                class="lf-input"
                @focus="passFocused = true"
                @blur="passFocused = false"
                autocomplete="off"
                dir="ltr"
              />
              <button type="button" class="lf-eye" @click="showPassword = !showPassword" tabindex="-1">
                <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Submit -->
          <button type="submit" class="lc-submit" :class="{ 'lc-submit--loading': submitting }">
            <span v-if="!submitting">🚀 تسجيل الدخول</span>
            <span v-else class="lc-spinner"></span>
          </button>

        </form>

        <!-- Search reservation link -->
        <!-- <p class="lc-foot">
          لم تشترك بعد؟
          <router-link to="/search" class="lc-link">ابحث عن حجزك</router-link>
        </p> -->

        <!-- Legal links -->
        <p class="lc-legal">
          بتسجيل الدخول فإنك توافق على
          <router-link to="/terms-of-service" class="lc-link">شروط الاستخدام</router-link>
          و
          <router-link to="/privacy-policy" class="lc-link">سياسة الخصوصية</router-link>
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: "login-auth",
  data() {
    return {
      reservationNumber: "",
      password: "",
      showPassword: false,
      phoneFocused: false,
      passFocused: false,
      submitting: false,
      shaking: false,
    };
  },
  methods: {
    onMouseMove(e) {
      const panel = this.$refs.decoPanel;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      panel.style.setProperty('--mx', dx.toFixed(3));
      panel.style.setProperty('--my', dy.toFixed(3));
    },

    shake() {
      this.shaking = true;
      setTimeout(() => { this.shaking = false; }, 600);
    },

    async login() {
      if (this.reservationNumber === "" || this.password === "") {
        this.shake();
        Swal.fire({ position: "center", icon: "error", title: "برجاء التحقق من كل البيانات" });
        return;
      }

      this.submitting = true;
      try {
        const formData = new FormData();
        formData.append('reservation_number', this.reservationNumber);
        formData.append('password', this.password);
        const response = await this.$store.dispatch("Login", formData);

        if (response.data.status === true) {
          Swal.fire({ position: "center", icon: "success", title: response.data.message });
          this.reservationNumber = "";
          this.password = "";
          this.$router.go('/');
        } else if (response.status === 422) {
          this.shake();
          Swal.fire({ position: "center", icon: "warning", title: response.data.message });
        } else if (response.data.status === false) {
          this.shake();
          Swal.fire({ position: "center", icon: "error", title: response.data.message });
        }
      } catch (err) {
        this.shake();
        Swal.fire({ position: "center", icon: "error", title: err.response?.data?.message || "حدث خطأ، حاول مجدداً" });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ─── Page layout ─── */
.login-page {
  display: flex;
  flex-direction: row;           /* RTL: first child = right side */
  min-height: 100vh;
  overflow: hidden;
  font-family: 'Cairo', sans-serif;
  direction: rtl;
}

/* ════════════════════════════════════════
   DECORATIVE PANEL (right)
════════════════════════════════════════ */
.login-deco {
  flex: 1;
  position: relative;
  background: linear-gradient(135deg, #02090f 0%, #051629 35%, #F7B424 65%, #FBCE72 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2.5rem;
  gap: 2rem;
  --mx: 0;
  --my: 0;
}

/* Blobs */
.ld-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.4;
  pointer-events: none;
  animation: ldBlobFloat 8s ease-in-out infinite;
}
.ld-blob-1 {
  width: 420px; height: 420px;
  background: radial-gradient(circle, #F7B424, transparent);
  top: -100px; right: -80px;
  animation-duration: 9s;
}
.ld-blob-2 {
  width: 350px; height: 350px;
  background: radial-gradient(circle, #00D4A6, transparent);
  bottom: -60px; left: -60px;
  animation-duration: 11s; animation-delay: -3s;
}
.ld-blob-3 {
  width: 250px; height: 250px;
  background: radial-gradient(circle, #f472b6, transparent);
  top: 50%; left: 50%; transform: translate(-50%, -50%);
  animation-duration: 7s; animation-delay: -5s;
}
@keyframes ldBlobFloat {
  0%, 100% { transform: scale(1) translate(0, 0); }
  33% { transform: scale(1.08) translate(15px, -20px); }
  66% { transform: scale(0.95) translate(-10px, 15px); }
}

/* Floating math symbols */
.lm {
  position: absolute;
  font-size: 2rem;
  font-weight: 800;
  color: rgba(255,255,255,0.15);
  pointer-events: none;
  animation: ldFloat 6s ease-in-out infinite;
  user-select: none;
}
.lm-1  { top: 8%;  right: 12%; font-size: 2.5rem; animation-duration: 7s; }
.lm-2  { top: 15%; left: 18%;  font-size: 3rem;   animation-duration: 9s; animation-delay: -1s; }
.lm-3  { top: 25%; right: 5%;  font-size: 1.8rem; animation-duration: 6s; animation-delay: -2s; }
.lm-4  { top: 40%; left: 8%;   font-size: 2.2rem; animation-duration: 8s; animation-delay: -3s; }
.lm-5  { top: 60%; right: 10%; font-size: 3.5rem; animation-duration: 10s; animation-delay: -1.5s; }
.lm-6  { top: 72%; left: 15%;  font-size: 2rem;   animation-duration: 7s; animation-delay: -4s; }
.lm-7  { top: 80%; right: 20%; font-size: 1.6rem; animation-duration: 6s; animation-delay: -2.5s; }
.lm-8  { top: 90%; left: 30%;  font-size: 2.8rem; animation-duration: 9s; animation-delay: -0.5s; }
.lm-9  { top: 5%;  left: 40%;  font-size: 1.4rem; animation-duration: 8s; animation-delay: -3.5s; }
.lm-10 { top: 50%; right: 30%; font-size: 2rem;   animation-duration: 11s; animation-delay: -2s; }
.lm-11 { top: 30%; left: 30%;  font-size: 1.6rem; animation-duration: 7s; animation-delay: -4.5s; }
.lm-12 { top: 85%; right: 40%; font-size: 2.4rem; animation-duration: 9s; animation-delay: -1s; }
@keyframes ldFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.15; }
  50% { transform: translateY(-22px) rotate(8deg); opacity: 0.28; }
}

/* Avatar */
.ld-avatar-wrap {
  position: relative;
  width: 160px; height: 160px;
  flex-shrink: 0;
  z-index: 2;
  transform: translateX(calc(var(--mx) * -12px)) translateY(calc(var(--my) * -8px));
  transition: transform 0.1s ease-out;
}
.ld-avatar-orbit {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.25);
  animation: ldOrbit 8s linear infinite;
}
.ld-orbit-1 {
  inset: -16px;
  border-color: rgba(255,255,255,0.15);
  animation-duration: 10s;
}
.ld-orbit-2 {
  inset: -30px;
  border-color: rgba(0,194,255,0.2);
  animation-duration: 14s;
  animation-direction: reverse;
}
@keyframes ldOrbit {
  to { transform: rotate(360deg); }
}
.ld-avatar-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%);
  padding: 6px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.3);
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.ld-avatar-ring img {
  width: 100%; height: 100%;
  object-fit: cover; border-radius: 50%;
}

/* Deco text */
.ld-text {
  text-align: center;
  z-index: 2;
  transform: translateX(calc(var(--mx) * -6px));
  transition: transform 0.1s ease-out;
}
.ld-heading {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 0.5rem;
  text-shadow: 0 4px 20px rgba(0,0,0,0.2);
}
.ld-sub {
  font-size: 1rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 1.5rem;
}
.ld-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
}
.ld-badge {
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.25);
  color: #fff;
  padding: 0.35rem 0.9rem;
  border-radius: 99px;
  font-size: 0.82rem;
  font-weight: 700;
}

/* ════════════════════════════════════════
   FORM PANEL (left)
════════════════════════════════════════ */
.login-form-panel {
  width: 480px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F9F9F9;
  padding: 2rem 1.5rem;
  position: relative;
}

/* Card */
.login-card {
  width: 100%;
  max-width: 400px;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(20px);
  border-radius: 28px;
  padding: 2.5rem 2rem;
  box-shadow: 0 24px 80px rgba(5, 22, 41,0.12), 0 4px 16px rgba(0,0,0,0.06);
  border: 1px solid rgba(255,255,255,0.8);
  transition: transform 0.3s ease;
}
.login-card--shake {
  animation: lcShake 0.5s cubic-bezier(.36,.07,.19,.97);
}
@keyframes lcShake {
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-10px); }
  30% { transform: translateX(10px); }
  45% { transform: translateX(-8px); }
  60% { transform: translateX(8px); }
  75% { transform: translateX(-5px); }
  90% { transform: translateX(5px); }
}

/* Logo */
.lc-logo {
  width: 64px; height: 64px;
  border-radius: 18px;
  background: linear-gradient(135deg, #051629, #F7B424);
  padding: 10px;
  margin: 0 auto 1.25rem;
  box-shadow: 0 12px 30px rgba(5, 22, 41,0.35);
  overflow: hidden;
}
.lc-logo img {
  width: 100%; height: 100%; object-fit: contain;
}

.lc-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1F2937;
  text-align: center;
  margin-bottom: 0.3rem;
}
.lc-subtitle {
  font-size: 0.9rem;
  color: #6B7280;
  text-align: center;
  margin-bottom: 2rem;
}

/* Form */
.lc-form { display: flex; flex-direction: column; gap: 1.25rem; }

/* Field group */
.lf-group { position: relative; }

.lf-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #6B7280;
  margin-bottom: 0.45rem;
  transition: color 0.25s;
}
.lf-group--focus .lf-label { color: #051629; }

.lf-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.lf-icon {
  position: absolute;
  right: 14px;
  width: 20px; height: 20px;
  color: #9CA3AF;
  pointer-events: none;
  transition: color 0.25s;
  display: flex; align-items: center; justify-content: center;
}
.lf-icon svg { width: 18px; height: 18px; }
.lf-group--focus .lf-icon { color: #051629; }

.lf-input {
  width: 100%;
  padding: 0.85rem 2.75rem 0.85rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 14px;
  font-family: 'Cairo', sans-serif;
  font-size: 0.95rem;
  color: #1F2937;
  background: #F9FAFB;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
  text-align: left;
}
.lf-input:focus {
  border-color: #051629;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(5, 22, 41,0.12);
}
.lf-input::placeholder { color: #C4C9D4; }

/* Eye toggle */
.lf-eye {
  position: absolute;
  left: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  padding: 4px;
  display: flex; align-items: center; justify-content: center;
  transition: color 0.2s;
}
.lf-eye:hover { color: #051629; }
.lf-eye svg { width: 18px; height: 18px; }

/* Submit button */
.lc-submit {
  width: 100%;
  padding: 0.95rem;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #051629, #F7B424);
  color: #fff;
  font-family: 'Cairo', sans-serif;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(5, 22, 41,0.35);
  transition: all 0.3s;
  display: flex; align-items: center; justify-content: center;
  margin-top: 0.25rem;
  min-height: 54px;
}
.lc-submit:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 20px 45px rgba(5, 22, 41,0.45);
}
.lc-submit:active { transform: translateY(-1px); }
.lc-submit--loading { opacity: 0.85; cursor: not-allowed; }

.lc-spinner {
  width: 24px; height: 24px;
  border: 3px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lcSpin 0.8s linear infinite;
  display: inline-block;
}
@keyframes lcSpin { to { transform: rotate(360deg); } }

/* Footer link */
.lc-foot {
  text-align: center;
  font-size: 0.85rem;
  color: #9CA3AF;
  margin-top: 1.25rem;
}
.lc-link {
  color: #051629;
  font-weight: 700;
  text-decoration: none;
  transition: color 0.2s;
}
.lc-link:hover { color: #F7B424; }

/* Legal links under the form */
.lc-legal {
  text-align: center;
  font-size: 0.78rem;
  color: #9CA3AF;
  line-height: 1.7;
  margin-top: 0.85rem;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .login-page { flex-direction: column; }
  .login-deco { min-height: 300px; padding: 2.5rem 1.5rem; gap: 1.25rem; }
  .ld-avatar-wrap { width: 110px; height: 110px; }
  .ld-heading { font-size: 1.6rem; }
  .login-form-panel { width: 100%; padding: 2rem 1.25rem 3rem; }
  .login-card { max-width: 480px; }
  /* hide many floating symbols on mobile */
  .lm-7, .lm-8, .lm-9, .lm-10, .lm-11, .lm-12 { display: none; }
}

@media (max-width: 480px) {
  .login-card { padding: 2rem 1.25rem; border-radius: 20px; }
  .lc-title { font-size: 1.35rem; }
  .ld-badges { gap: 0.4rem; }
  .ld-badge { font-size: 0.75rem; }
}
</style>
